import { getCurriculum } from "./curriculum";
import { getGrammarPoints, buildQuestions as buildGrammarQuestions } from "./grammarLab";

// CEFR tiers present in the current curriculum, in ascending difficulty order.
export const TIERS = ["A1", "A2", "B1", "B2"];

// 4 rounds x 5 questions (2 word-translation + 2 listening + 1 grammar) = 20
// questions total, reusing only content already generated for the curriculum
// and Grammar Lab — this module itself never calls an AI model. For non-English
// base languages, PlacementTestView.js separately translates each round's word/
// listening/grammar answer text (see choiceItems/promptItem below) via a
// cache-first Haiku call before showing it — that pipeline lives at the view
// layer, not here.
export const ROUNDS = 4;
export const QUESTIONS_PER_ROUND = 5;

// Starting tier: the lower of the two middle tiers (A2, index 1 of 4 — TIERS
// has no true midpoint). Most learners taking a placement test on a
// beginner-oriented app are more likely near-beginners with some prior
// exposure than genuine intermediates, so a false-beginner overshooting into
// B1 is a worse first impression than a real intermediate needing one extra
// round to climb from A2. This is a tunable assumption, not a derived value —
// see the placement-test build report for the alternative (B1) and how to
// flip it (just change this constant).
export const START_TIER_INDEX = 1;

// A round's outcome adjusts the tier by at most one step, biased toward
// under-placing rather than over-placing (a false-beginner redoing one easy
// round costs far less than a false-intermediate stuck lost in material
// above their level). Of 5 questions: 4-5 correct moves up, 0-2 correct moves
// down, and only the exact middle score (3 correct) holds the tier steady
// for one more confirming round.
const UP_THRESHOLD = 0.8;
const DOWN_THRESHOLD = 0.4;

function wordsForTier(tier, langCode) {
  const out = [];
  for (const topic of getCurriculum(langCode)) {
    for (const w of topic.words) {
      if (w.cefr_level === tier && w.is_flashcard_eligible !== false && w.turkish && w.english) {
        out.push({ ...w, topicOrder: topic.order, topicName: topic.topic, topicId: topic.topic_id });
      }
    }
  }
  return out;
}

// Tiers ordered by distance from `tier`, closest first — used to widen the
// question/distractor pool whenever a single tier's own pool is too small
// (e.g. B2 has only 6 topics), so a sparse tier never repeats content or
// runs out of distinct distractors within one test session.
function tiersByDistance(tier) {
  const i = TIERS.indexOf(tier);
  return [...TIERS].sort((a, b) => Math.abs(TIERS.indexOf(a) - i) - Math.abs(TIERS.indexOf(b) - i));
}

function pickRandom(arr, n) {
  return [...arr].sort(() => Math.random() - 0.5).slice(0, n);
}

function buildWordQuestion(word, pool) {
  const uniqueEnglish = [...new Map(
    pool.filter(w => w.word_id !== word.word_id && w.english !== word.english).map(w => [w.english, w])
  ).values()];
  const distractors = pickRandom(uniqueEnglish, 3);
  const choiceWords = pickRandom([word, ...distractors], distractors.length + 1);
  return {
    source: "word", id: `word_${word.word_id}`, wordId: word.word_id, prompt: word.turkish,
    answer: word.english, options: choiceWords.map(w => w.english),
    // Positional identity of the correct choice, independent of its display text — English
    // distractors are guaranteed unique (filtered above), but their *translations* aren't
    // (two different English words can translate to the same word in another language), so
    // PlacementTestView keys selection/correctness off this index rather than string equality.
    answerIndex: choiceWords.findIndex(w => w.word_id === word.word_id),
    // English text keyed by word_id, for translatePlacementRound (api.js/PlacementTestView)
    // to translate into the learner's base language without re-deriving which option was which.
    choiceItems: choiceWords.map(w => ({ id: w.word_id, text: w.english })),
  };
}

// Answer options are base-language translations, not target-language surface forms — showing
// Turkish text here would let the learner visually match the spoken word's spelling instead of
// testing whether they understood it by ear. `prompt` stays word.turkish (that's what TTS speaks
// and what the word is actually being tested on); only the 4 answer choices differ from before.
// Structurally this mirrors buildWordQuestion exactly (same choiceItems/answerIndex shape) so
// PlacementTestView's translatePlacementRound can translate both question types identically.
function buildListeningQuestion(word, pool) {
  const uniqueEnglish = [...new Map(
    pool.filter(w => w.word_id !== word.word_id && w.english !== word.english).map(w => [w.english, w])
  ).values()];
  const distractors = pickRandom(uniqueEnglish, 3);
  const choiceWords = pickRandom([word, ...distractors], distractors.length + 1);
  return {
    source: "listening", id: `listen_${word.word_id}`, wordId: word.word_id, prompt: word.turkish,
    answer: word.english, options: choiceWords.map(w => w.english),
    answerIndex: choiceWords.findIndex(w => w.word_id === word.word_id),
    choiceItems: choiceWords.map(w => ({ id: w.word_id, text: w.english })),
  };
}

// Builds one round of QUESTIONS_PER_ROUND questions at `tier`. usedWordIds /
// usedGrammarIds are Sets mutated in place so no word or grammar point is
// asked about twice across the whole test.
export function buildRound(tier, usedWordIds, usedGrammarIds, langCode = "turkish") {
  const questions = [];
  const tierOrder = tiersByDistance(tier);

  const wordPool = [];
  for (const t of tierOrder) {
    wordPool.push(...wordsForTier(t, langCode).filter(w => !usedWordIds.has(w.word_id)));
    if (wordPool.length >= 8) break; // enough for 4 picks + 3 distractors each
  }
  const picks = pickRandom(wordPool, Math.min(4, wordPool.length));
  picks.forEach(w => usedWordIds.add(w.word_id));
  const [t1, t2, l1, l2] = picks;
  if (t1) questions.push(buildWordQuestion(t1, wordPool));
  if (t2) questions.push(buildWordQuestion(t2, wordPool));
  if (l1) questions.push(buildListeningQuestion(l1, wordPool));
  if (l2) questions.push(buildListeningQuestion(l2, wordPool));

  let gpPool = [];
  for (const t of tierOrder) {
    gpPool = getGrammarPoints(langCode).filter(gp => gp.cefr_level === t && !usedGrammarIds.has(gp.grammar_point_id) && gp.point_type !== "structural");
    if (gpPool.length) break;
  }
  if (gpPool.length) {
    const gp = pickRandom(gpPool, 1)[0];
    usedGrammarIds.add(gp.grammar_point_id);
    const [gq] = buildGrammarQuestions(gp, 1, langCode);
    if (gq) {
      const promptItemId = `${gp.grammar_point_id}_${gq.formId}`;
      questions.push({
        source: "grammar", id: `grammar_${promptItemId}`, wordId: null, prompt: gq.prompt, answer: gq.answer, options: gq.options,
        // English meaning gloss, for translatePlacementRound to translate — see buildWordQuestion's
        // choiceItems above for why this stays a separate field rather than mutating `prompt` here.
        promptItem: { id: promptItemId, text: gq.prompt },
      });
    }
  }

  // Backfill: if both the word pool and the grammar pool came up short at
  // every tier distance (only plausible if the test has already burned
  // through most of a very sparse tier's content), pad with extra
  // word-translation questions so every round still reaches
  // QUESTIONS_PER_ROUND and round-to-round scoring stays comparable.
  while (questions.length < QUESTIONS_PER_ROUND) {
    const remaining = wordPool.filter(w => !usedWordIds.has(w.word_id));
    const fallbackPool = remaining.length ? remaining : TIERS.flatMap(wordsForTier).filter(w => !usedWordIds.has(w.word_id));
    const w = pickRandom(fallbackPool, 1)[0];
    if (!w) break; // truly nothing left unused — round ships short rather than repeating content
    usedWordIds.add(w.word_id);
    questions.push(buildWordQuestion(w, fallbackPool));
  }

  return questions.slice(0, QUESTIONS_PER_ROUND);
}

// Deterministic tier movement from one round's raw score — no AI call.
export function nextTier(tier, correctCount, totalCount) {
  const ratio = totalCount ? correctCount / totalCount : 0;
  const i = TIERS.indexOf(tier);
  if (ratio >= UP_THRESHOLD) return TIERS[Math.min(i + 1, TIERS.length - 1)];
  if (ratio <= DOWN_THRESHOLD) return TIERS[Math.max(i - 1, 0)];
  return tier;
}

// First topic (by curriculum order) tagged with the placed tier — the new
// Current Lesson / All Lessons starting point. Earlier and later topics stay
// fully browsable; this never writes a lock, matching the app's existing
// no-hard-gating design (the only lock anywhere is the 60+ due-card
// overwhelm gate).
export function pickStartingTopic(tier, langCode) {
  const curriculum = getCurriculum(langCode);
  const inTier = curriculum.filter(t => t.cefr_level === tier).sort((a, b) => a.order - b.order);
  return inTier[0] || curriculum[0];
}
